import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpErrorResponse } from '@angular/common/http';
import { Router } from '@angular/router';
import { BehaviorSubject, EMPTY, Observable, Subject, throwError, MonoTypeOperatorFunction } from 'rxjs';
import { tap, catchError } from 'rxjs/operators';
import { Configuration } from '../../../app.constants';
import {
  ExternalShareViewModel,
  OtpRequestResponseViewModel,
  ValidateOtpRequestViewModel,
  ValidateOtpResponseViewModel,
  ExternalSessionData,
  ExternalTokenClaims,
  ExternalObjectType
} from '../models/external-token.model';

const STORAGE_KEYS = {
  TOKEN: 'external_access_token',
  SESSION: 'external_session_data'
};

@Injectable({
  providedIn: 'root'
})
export class ExternalAuthService {
  private baseUrl: string;

  private currentShareData = new BehaviorSubject<ExternalShareViewModel | null>(null);
  public currentShareData$ = this.currentShareData.asObservable();

  private currentSession = new BehaviorSubject<ExternalSessionData | null>(null);
  public currentSession$ = this.currentSession.asObservable();

  private isLoading = new BehaviorSubject<boolean>(false);
  public isLoading$ = this.isLoading.asObservable();
  private isRedirecting = false;

  private _methodNotAllowed = new Subject<{ isDue: boolean; isCompleted: boolean }>();
  public methodNotAllowed$ = this._methodNotAllowed.asObservable();

  constructor(
    private http: HttpClient,
    private router: Router,
    private configuration: Configuration
  ) {
    this.baseUrl = this.configuration.buildEndpoint('ExternalAccess/');
    this.restoreSession();
  }

  // ============ API CALLS ============

  /**
   * Step 1: Get External Share Information (Anonymous)
   * GET /v1/ExternalAccess/{externalShareId}
   */
  getExternalShare(externalShareId: string): Observable<ExternalShareViewModel> {
    this.isLoading.next(true);
    return this.http.get<ExternalShareViewModel>(`${this.baseUrl}${externalShareId}`).pipe(
      tap(shareData => {
        this.currentShareData.next(shareData);
        this.isLoading.next(false);
      }),
      catchError(error => {
        this.isLoading.next(false);
        return throwError(() => error);
      })
    );
  }

  /**
   * Step 2: Request OTP (Anonymous)
   * POST /v1/ExternalAccess/{externalShareId}/request-otp
   */
  requestOtp(externalShareId: string): Observable<OtpRequestResponseViewModel> {
    this.isLoading.next(true);
    return this.http.post<OtpRequestResponseViewModel>(
      `${this.baseUrl}${externalShareId}/request-otp`,
      {}
    ).pipe(
      tap(() => this.isLoading.next(false)),
      catchError(error => {
        this.isLoading.next(false);
        return throwError(() => error);
      })
    );
  }

  /**
   * Step 3: Validate OTP and Get Token (Anonymous)
   * POST /v1/ExternalAccess/{externalShareId}/validate-otp
   */
  validateOtp(externalShareId: string, otpCode: string): Observable<ValidateOtpResponseViewModel> {
    this.isLoading.next(true);
    const request: ValidateOtpRequestViewModel = {
      externalShareId,
      otpCode
    };

    return this.http.post<ValidateOtpResponseViewModel>(
      `${this.baseUrl}${externalShareId}/validate-otp`,
      request
    ).pipe(
      tap(response => {
        if (response.success && response.token) {
          this.storeSession(response, externalShareId);
        }
        this.isLoading.next(false);
      }),
      catchError(error => {
        this.isLoading.next(false);
        return throwError(() => error);
      })
    );
  }

  /**
   * Step 4: Accept Policies (Authenticated)
   * POST /v1/ExternalPolicies/{externalShareId}/accept
   */
  acceptPolicies(externalShareId: string): Observable<ExternalShareViewModel> {
    this.isLoading.next(true);
    const policiesUrl = this.configuration.buildEndpoint('ExternalPolicies/');

    return this.http.post<ExternalShareViewModel>(
      `${policiesUrl}${externalShareId}/accept`,
      {},
      { headers: this.getAuthHeaders() }
    ).pipe(
      tap(shareData => {
        this.currentShareData.next(shareData);
        this.updateSessionPolicyAccepted(true);
        this.isLoading.next(false);
      }),
      catchError(error => {
        this.isLoading.next(false);
        return this.handleUnauthorizedError(error);
      })
    );
  }

  // ============ SESSION MANAGEMENT ============

  private storeSession(response: ValidateOtpResponseViewModel, externalShareId: string): void {
    if (!response.token) return;

    const shareData = this.currentShareData.value;

    let expiresAt: Date;
    if (response.expirationInHours != null && response.expirationInHours > 0) {
      expiresAt = new Date(Date.now() + response.expirationInHours * 60 * 60 * 1000);
    } else {
      const claims = this.decodeToken(response.token);
      expiresAt = new Date(claims.exp * 1000);
    }

    const sessionData: ExternalSessionData = {
      token: response.token,
      externalShareId: externalShareId,
      externalEmployeeId: response.externalEmployeeId!,
      accountId: response.accountId!,
      objectType: response.objectType!,
      objectId: response.objectId!,
      isPolicyAccepted: shareData?.isPolicyAccepted ?? false,
      expiresAt: expiresAt
    };

    localStorage.setItem(STORAGE_KEYS.TOKEN, response.token);
    localStorage.setItem(STORAGE_KEYS.SESSION, JSON.stringify(sessionData));
    this.currentSession.next(sessionData);
  }

  private restoreSession(): void {
    try {
      const token = localStorage.getItem(STORAGE_KEYS.TOKEN);
      const sessionJson = localStorage.getItem(STORAGE_KEYS.SESSION);

      if (token && sessionJson) {
        const sessionData: ExternalSessionData = JSON.parse(sessionJson);
        sessionData.expiresAt = new Date(sessionData.expiresAt);

        if (!this.isTokenExpired(sessionData.expiresAt)) {
          this.currentSession.next(sessionData);
        } else {
          this.clearSession();
        }
      }
    } catch {
      this.clearSession();
    }
  }

  private updateSessionPolicyAccepted(accepted: boolean): void {
    const session = this.currentSession.value;
    if (session) {
      session.isPolicyAccepted = accepted;
      localStorage.setItem(STORAGE_KEYS.SESSION, JSON.stringify(session));
      this.currentSession.next(session);
    }
  }

  // ============ TOKEN UTILITIES ============

  private decodeToken(token: string): ExternalTokenClaims {
    const payload = token.split('.')[1];
    const decoded = atob(payload);
    return JSON.parse(decoded);
  }

  private isTokenExpired(expiresAt: Date): boolean {
    // Add 1-minute buffer for network latency
    return new Date() >= new Date(expiresAt.getTime() - 60000);
  }

  getToken(): string | null {
    const token = localStorage.getItem(STORAGE_KEYS.TOKEN);
    return token;
  }

  isAuthenticated(): boolean {
    const session = this.currentSession.value;
    if (!session) {
      return false;
    }
    const expired = this.isTokenExpired(session.expiresAt);
    return !expired;
  }

  getCurrentSession(): ExternalSessionData | null {
    return this.currentSession.value;
  }

  getCurrentShareData(): ExternalShareViewModel | null {
    return this.currentShareData.value;
  }

  // ============ AUTH HEADERS ============

  private getAuthHeaders(): HttpHeaders {
    const token = this.getToken();
    return new HttpHeaders({
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    });
  }

  // ============ LOGOUT / CLEAR ============

  clearSession(): void {
    localStorage.removeItem(STORAGE_KEYS.TOKEN);
    localStorage.removeItem(STORAGE_KEYS.SESSION);
    this.currentSession.next(null);
    this.currentShareData.next(null);
  }

  logout(): void {
    this.clearSession();
  }

  // ============ ERROR HANDLING ============

  /**
   * Handles 405 Method Not Allowed errors from external endpoints.
   * Emits on methodNotAllowed$ so components can show the appropriate overlay.
   */
  handleMethodNotAllowed(error: HttpErrorResponse): Observable<never> {
    let body = error.error ?? {};
    if (typeof body === 'string') {
      try {
        body = JSON.parse(body);
      } catch {
        body = {};
      }
    }
    const isDue = body.IsDue === true || body.isDue === true;
    const isCompleted = body.IsCompleted === true || body.isCompleted === true;
    this._methodNotAllowed.next({ isDue, isCompleted });
    return EMPTY;
  }

  /**
   * Handles 401 Unauthorized errors for external access API calls.
   * Clears the session and navigates to the landing page to restart OTP flow.
   * @param error The HTTP error response
   * @returns Observable that throws the error for further handling
   */
  handleUnauthorizedError(error: HttpErrorResponse): Observable<never> {
    if (this.isRedirecting) {
      return throwError(() => error);
    }

    if (error.status === 401) {
      this.isRedirecting = true;

      const currentUrl = this.router.url;

      let shareId = this.currentSession.value?.externalShareId;
      if (!shareId) {
        const urlMatch = currentUrl.match(/\/external-access\/([^\/]+)/);
        shareId = urlMatch ? urlMatch[1] : undefined;
      }

      this.clearSession();

      if (shareId) {
        void this.router.navigate(['/external-access', shareId]);
      } else {
        void this.router.navigate(['/external-access']);
      }

      document.dispatchEvent(new KeyboardEvent('keydown', { key: 'Escape', bubbles: true }));

      return throwError(() => error);
    } else {
      return throwError(() => error);
    }

  }

  /**
   * RxJS operator for handling 401 errors in Observable pipes.
   * Usage: .pipe(this.externalAuthService.catchUnauthorized())
   * @returns MonoTypeOperatorFunction that catches 401 errors
   */
  catchUnauthorized<T>(): MonoTypeOperatorFunction<T> {
    return catchError((error: HttpErrorResponse) => {
      return this.handleUnauthorizedError(error);
    });
  }
}
